"use client";

import ApertureBg from "@/public/imaegs/download-aperture.svg";
import { FellouButton } from "@/ui-components-next/FellouButton";
import Image from "next/image";
import { adjustTracking } from "@/services/adjust";
import { useDownloadContext } from "@/contexts/DownloadContext";
import "./styles.scss";

export default function DownloadFirstScreen() {
  const { downloadInfo, isLoading, error } = useDownloadContext();

  const downloadFile = (url: string, platform: string) => {
    // Check if data is loading
    if (isLoading) {
      console.log("Download data still loading, please wait...");
      return;
    }

    // Track download events - both download and downloadOnly
    adjustTracking.trackDownload({
      platform,
      source: "download-page",
      timestamp: new Date().toISOString(),
    });

    adjustTracking.trackDownloadOnly({
      platform,
      source: "download-page",
      timestamp: new Date().toISOString(),
    });

    // Track platform-specific events
    if (platform === "mac_arm") {
      adjustTracking.trackMacAppleDownload({
        platform,
        source: "download-page",
        timestamp: new Date().toISOString(),
      });
    } else if (platform === "mac_intel") {
      adjustTracking.trackMacIntelDownload({
        platform,
        source: "download-page",
        timestamp: new Date().toISOString(),
      });
    } else if (platform === "windows") {
      adjustTracking.trackWindowsDownload({
        platform,
        source: "download-page",
        timestamp: new Date().toISOString(),
      });
    }

    // Execute download
    const link = document.createElement("a");
    link.href = url;
    link.download = url.split("/").pop() || "download";
    link.click();

    const channel = localStorage.getItem("utm_source");

    // Keep backend tracking
    fetch("https://admin.fellou.ai/api/download/track", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ platform, channel }),
    }).catch((error) => console.error("Error tracking download:", error));
  };

  return (
    <div className="download-first-screen-box">
      <div className="download-screen-bg">
        <Image className="download-screen-bg-img" src={ApertureBg} alt="fellou" fill loading="lazy" />
      </div>

      <div className="download-screen-1-box">
        <div className="download-logo-icon-box">
          <div className={"download-logo-box "}>
            <Image
              src={`/favicon.ico`}
              alt="fellou"
              fill
              priority
              unoptimized
            />
          </div>
        </div>

        <div className="screen-1-info-box">
          <div className="download-title">
            <div className="download-title-1">Download Fellou Browser</div>
            <div className="download-title-2">
              Click on the Apple menu in the upper left corner of the Mac computer, in the{" "}
              <span className="download-title-2-chip">&quot;About this machine&quot;</span>section, you can see
              <br />
              the <span className="download-title-2-chip">&quot;chip typel&quot;</span>. Confirm whether{" "}
              <span className="download-title-2-chip">&quot;Intel&quot;</span> or{" "}
              <span className="download-title-2-chip">&quot;Apple&quot;</span> is displayed and download the
              corresponding version
            </div>
          </div>

          <div className={"download-info-box "}>
            {isLoading ? (
              // Skeleton loading for download items
              <>
                {[1, 2].map((skeletonIndex) => (
                  <div className="download-info-item-box skeleton-item" key={`skeleton-${skeletonIndex}`}>
                    <div className="download-info-item-box-icon">
                      <div className="skeleton-icon"></div>
                    </div>
                    <div className="skeleton-title"></div>
                    <div className="skeleton-hint"></div>
                    <div className="download-info-item-box-btns">
                      <div className="download-btn-box">
                        <div className="skeleton-button"></div>
                        <div className="skeleton-button"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            ) : error ? (
              <div className="error-message">Error loading download info: {error}</div>
            ) : (
              downloadInfo.map((item, index) => (
                <div className="download-info-item-box" key={index}>
                  <div className="download-info-item-box-icon">
                    <i className={`icon iconfont ${item.icon}`}></i>
                  </div>
                  <div className="download-info-item-box-title">{item.title}</div>
                  <div className="download-info-item-box-hint">{item.hint}</div>
                  <div className="download-info-item-box-btns">
                    <div className={"download-btn-box "}>
                      {item.resource.map((resource, resourceIndex) => (
                        <FellouButton
                          key={resourceIndex}
                          onClick={() => downloadFile(resource.url, resource.platform)}
                          className="download-consult-btn cursor-pointer"
                          mode="dominant-color-big"
                        >
                          <i className={`icon iconfont icon-download-line`}></i>
                          {resource.text.replace("Download ", " ")}
                        </FellouButton>
                      ))}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
